import React from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { StateIconIndicator } from "../ProcessIndicator/ProcessIndicator";
import { getStateByID } from "../../functions/getStateByID";
import { getTimeFromNow } from "../../functions/time";
import { sanitizeAllowed } from "../../functions/html";
import {
  Typography,
  Avatar,
  Chip,
  ListItemButton,
  ListItemText,
} from "@mui/material";

import { getImageSRC, getMimeType } from "../../functions/assets";
import { selectEstateById } from "../../modules/estate/estateList/estateListSlice";
import { accountSelectors } from "../../modules/accounts/accountSlice";
import { ROLES } from "../../modules/accounts/accountConstants";
import { accountHasRole } from "../../modules/accounts/accountFunctions";
import { ticketIsPrivate } from "../../functions/ticketIsPrivate";
import { history } from "../../store/store";
import { setTicketRead } from "../../modules/ticket/ticketsRead/ticketsReadActions";

const FolderItemView = (props) => {
  const theme = useTheme();
  const {
    id,
    subject,
    body,
    attachments,
    createdAt,
    processingState,
    isMarked,
    dense,
    estate,
    userIsTicketManager,
    setTicketRead,
  } = props;

  const dotBg = isMarked ? "orange" : "lightgrey";
  const fontWeight = isMarked ? "bold" : "normal";

  const isPrivate = ticketIsPrivate(props);
  const estateAddress = estate && estate.address;

  const attachmentOne = Array.isArray(attachments) ? attachments[0] : undefined;

  const handleOpenTicket = (ticketId) => {
    if (setTicketRead) setTicketRead(ticketId);
    history.push(`/tickets/${ticketId}`);
  };

  return (
    <ListItemButton
      onClick={() => handleOpenTicket(id)}
      sx={{ display: "block" }}
      divider
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
          gap: 1,
          flexWrap: "nowrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: dotBg,
              mr: 1,
            }}
          />

          {createdAt && (
            <Typography
              variant="body2"
              sx={{
                color: "rgba(0,0,0,0.54)",
                fontWeight,
              }}
            >
              {createdAt}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
          {isPrivate && <Chip label="privat" sx={{ height: "1rem" }} />}
        </Box>

        <StateIconIndicator status={processingState} />
      </Box>

      {/* title row */}
      <Box sx={{ display: "flex" }}>
        <ListItemText
          primary={subject}
          secondary={
            !Boolean(dense) && (
              <Typography
                component="span"
                variant="body2"
                color="text.primary"
                dangerouslySetInnerHTML={{ __html: sanitizeAllowed(body) }}
              />
            )
          }
        />

        {attachmentOne && (
          <Avatar
            alt={getMimeType(attachmentOne.mimeType)}
            src={`${getImageSRC(attachmentOne.previewFilename)}&scalePercent=5`}
            sx={{ borderRadius: 0, border: "1px solid #ccc" }}
            loading="lazy"
          />
        )}
      </Box>

      {/* bottom row */}
      {userIsTicketManager && estateAddress && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(0,0,0,0.54)",
              fontSize: theme.typography.pxToRem(12),
            }}
          >
            {estateAddress.street} - {estateAddress.postalCode}{" "}
            {estateAddress.city}
          </Typography>
        </Box>
      )}
    </ListItemButton>
  );
};

const mapTicketPropsToListItem = (props) => {
  const { document, ...rest } = props;
  const stateType = getStateByID(document.processingState);
  const documentAuthor = !Boolean(document.anonymous) ? document.author : "";
  const createdAt = document.updatedAt
    ? getTimeFromNow(document.updatedAt)
    : getTimeFromNow(document.createdAt);

  return {
    ...document,
    ...rest,
    createdAt,
    stateType,
    isMarked: !props.readTicketsIds.includes(document.id),
    secondaryText: !Boolean(document.default) && documentAuthor,
  };
};

const mapStateToProps = (state, ownProps) => {
  const currentAccount = accountSelectors.selectCurrentAccount(state);
  const userIsTicketManager = accountHasRole(
    currentAccount,
    ROLES.TICKET_MANAGER,
  );
  return {
    estate: selectEstateById(state, ownProps.document.estateId),
    userIsTicketManager,
  };
};

// Connected wrapper: map and then render presentational component
const FolderItem = (props) => {
  const mapped = mapTicketPropsToListItem(props);
  return <FolderItemView {...mapped} setTicketRead={props.setTicketRead} />;
};

export const FolderItemContainer = connect(mapStateToProps, { setTicketRead })(
  FolderItem,
);
